import type { App } from "vue";
import { createVNode, defineComponent, getCurrentInstance, h, ref, render } from "vue";
import BaseDialog from "@/common/base-components/admin-pbl/BaseDialog.vue";
import { useI18n } from "vue-i18n";
import Swal, { type SweetAlertIcon, type SweetAlertResult } from "sweetalert2";
import StringUtil from "@/app/util/StringUtil";
import NumberUtil from "@/app/util/NumberUtil";
import FileUtil from "@/app/util/FileUtil";
import { getCmmnCdOption } from "@/api/cmmn/CmmnApi";
import i18ng from "@/i18n";
import { SearchType } from "@/types/Enums";
import type { TableHeader } from "@/common/types/TableInfoTypes";
import type { DetailField } from "@/types/guide/GuideTypes";
import type { CmmnCodeOptionType, cmmnOptionType } from "@/types/cmmn/CmmnTypes";
import { useLoginStore } from "@/app/store/LoginStore";
import type { FileMetaTypes } from "@/types/FileTypes";

/**
 * 현재 Vue 인스턴스의 글로벌 프로퍼티에 접근
 * @returns Vue 인스턴스의 글로벌 프로퍼티
 * @throws setup() 함수 내에서 호출되지 않은 경우 에러 발생
 */
export const global = () => {
  const instance = getCurrentInstance();
  if (!instance) {
    throw new Error("global must be called within setup()");
  }
  return instance.appContext.config.globalProperties;
};

/**
 * 다국어 처리를 위한 i18n 함수 반환
 * @returns i18n 번역 함수와 로케일 정보
 */
export const i18n = () => {
  const { t, locale } = useI18n();
  return { $text: t, locale };
};

/** 로딩 상태 관리 객체 */
const loading = {
  /** 로딩 상태를 활성화 */
  show() {
    window.$load.value = true;
  },
  /** 로딩 상태를 비활성화 */
  hide() {
    window.$load.value = false;
  },
};

/**
 * 필드 타입에 따른 기본값 반환
 * @param type - 필드 타입
 * @returns 타입에 맞는 기본값
 */
const processedValue = (type: DetailField["type"]) => {
  if (["text", "number", "longText", "radio", "date", "dateTime"].includes(type)) {
    return "";
  }
  if (type === "checkbox") {
    return [];
  }
  if (["autocomplete", "file", "popup"].includes(type)) {
    return null;
  }
  return "";
};

/**
 * DetailField 배열을 기반으로 파라미터 객체 생성
 * @param arr - DetailField 배열
 * @returns 키-값 쌍으로 구성된 파라미터 객체
 */
const setDetailValue = (arr: DetailField[]): ParamsType => {
  return arr.reduce((result: ParamsType, { key, type }: DetailField) => {
    if (Array.isArray(key)) {
      key.forEach((k: string) => (result[k] = processedValue(type)));
    } else {
      result[key] = processedValue(type);
    }
    return result;
  }, {});
};

/**
 * ArrayItem 배열을 기반으로 검색 파라미터 객체 생성
 * @param arr - ArrayItem 배열
 * @returns 키-값 쌍으로 구성된 파라미터 객체
 */
const setSearchValue = (arr: ArrayItem[]): ParamsType => {
  return arr.reduce((result: ParamsType, { key, value }: ArrayItem) => {
    if (Array.isArray(key)) {
      if (Array.isArray(value)) {
        key.forEach((k: string, i: number) => (result[k] = value[i] || ""));
      } else {
        key.forEach((k: string) => (result[k] = value));
      }
    } else {
      result[key] = value;
    }
    return result;
  }, {});
};

/**
 * 특정 키에 대한 검색 옵션 설정
 * @param arr - ArrayItem 배열
 * @param targetKey - 대상 키
 * @param newOption - 새로운 옵션 배열
 */
const setSearchOption = (arr: ArrayItem[], targetKey: string, newOption: []): void => {
  const target = arr.find((item) => item.key === targetKey);
  if (target) {
    target.options = newOption;
  }
};

/**
 * 배열 데이터를 옵션 리스트 형태로 변환
 * @param arr - 원본 데이터 배열
 * @param label - 라벨로 사용할 필드명
 * @param value - 값으로 사용할 필드명
 * @returns 변환된 옵션 리스트
 */
const setOptionList = (
  arr: [],
  label: string,
  value: string
): { label: string; value: string }[] => {
  return arr.map((data) => ({
    label: data[label] || "",
    value: data[value] || "",
  }));
};

/**
 * 비동기로 검색 옵션 리스트 설정
 * @param searchFields - 검색 필드 배열
 * @param options - 공통 옵션 배열
 * @returns 성공 여부
 */
const setSearchOptionList = async (
  searchFields: [],
  options: Array<cmmnOptionType>
): Promise<boolean> => {
  try {
    const upCdArr = [...new Set(options.map((item) => item.upCd))];
    if (upCdArr.length === 0) return true;
    const cmmnList = await getCmmnCdOption({ upCdArr: upCdArr });
    options.forEach((option) => {
      if (option.upCd === "use_yn00" && option.insert) {
        cmmnList[option.upCd] = cmmnList[option.upCd].filter(
          (item: CmmnCodeOptionType) => item.value !== "" && item.value !== null
        );
      }
      option.options = cmmnList[option.upCd];

      if (option.setAll) {
        option.options.unshift({ label: "전체", value: "" });
      }

      setSearchOption(searchFields, option.targetKey, cmmnList[option.upCd] as []);
    });
    return true;
  } catch (error) {
    console.error("Error setting search options:", error);
    throw error;
  }
};

/**
 * Vue 컴포넌트 정보 설정 (검색 필드, 파라미터, 테이블 헤더)
 * @param param - 필드 설정 객체
 * @param searchFields - 검색 필드 배열
 * @param searchParam - 검색 파라미터 객체
 * @param tableHeader - 테이블 헤더 배열
 * @param cmmnOptionList - 공통 옵션 리스트
 * @returns 성공 여부
 */
const setVueInfo = async (
  param: Record<string, unknown>,
  searchFields: [],
  searchParam: Record<string, unknown>,
  tableHeader: [],
  cmmnOptionList: cmmnOptionType[]
): Promise<boolean> => {
  const searchList = [] as ArrayItem[];
  const headList = [] as TableHeader[];
  const data = param as Record<string, FieldConfig>;
  Object.entries(data).forEach(([key, config]) => {
    if (config.head) {
      headList.push({
        ...config.head,
        text: config.label || key,
        value: key,
      });
    }
    if (config.search) {
      const sKey = setKey(key, config.search);
      searchList.push({
        key: sKey,
        value: setValue(config.search.type, sKey),
        label: config.label || key,
        ...config.search,
      });
    }
  });
  searchList.sort((a, b) => (a?.order || 0) - (b?.order || 0));
  await setSearchOptionList(searchList as [], cmmnOptionList);
  Object.assign(searchFields, searchList);
  Object.assign(searchParam, setSearchValue(searchList));
  Object.assign(tableHeader, headList);

  return true;
};

/**
 * 검색 타입에 따른 키 설정
 * @param key - 기본 키
 * @param search - 검색 설정 객체
 * @returns 설정된 키 (단일 또는 배열)
 */
const setKey = (key: string, search: { type: SearchType; group?: string }) => {
  if (search.type === SearchType.date || search.type === "number") {
    if (search.group) {
      return [key, search.group];
    }
    return [key];
  }
  return key;
};

/**
 * 검색 타입에 따른 기본값 설정
 * @param type - 검색 타입
 * @param key - 키 (단일 또는 배열)
 * @returns 타입에 맞는 기본값
 */
const setValue = (type: SearchType, key: string | string[]) => {
  if ([SearchType.text, SearchType.radio].includes(type)) {
    return "";
  } else if (type === SearchType.checkbox) {
    return [];
  } else if (type === SearchType.autocomplete) {
    return null;
  } else if (type === SearchType.number || type === SearchType.date) {
    if (Array.isArray(key) && key.length > 1) {
      return ["", ""];
    }
    return [""];
  }
};

/**
 * 쿠키설정
 * @param name - 쿠키 이름
 * @param value - 쿠키 값
 * @param expires - 만료 날짜 (선택사항)
 */
export const setCookie = (name: string, value: string, expires?: Date): void => {
  const expiresStr = expires ? `; expires=${expires.toUTCString()}` : "";
  document.cookie = `${name}=${value || ""}${expiresStr}; path=/`;
};

/**
 * 쿠키 삭제
 * @param names - 삭제할 쿠키 이름 배열
 */
export const delCookie = (names: string[]): void => {
  names.forEach((name) => {
    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
  });
};

/**
 * 쿠키조회
 * @param name - 쿠키 이름
 * @returns 쿠키 값 (존재하지 않으면 null)
 */
export const getCookie = (name: string): string | null => {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) {
    return parts.pop()?.split(";")[0] ?? null;
  }
  return null;
};

export default {
  install: (app: App) => {
    const { t, locale } = i18ng.global;

    window.$load = ref(false);
    window.$loading = loading;
    app.config.globalProperties.$StrUtil = StringUtil;
    app.config.globalProperties.$NumUtil = NumberUtil;
    app.config.globalProperties.$FileUtil = FileUtil;
    app.config.globalProperties.$setCookie = setCookie;
    app.config.globalProperties.$delCookie = delCookie;
    app.config.globalProperties.$getCookie = getCookie;
    app.config.globalProperties.$loginInfo = () => useLoginStore().getLoginInfo;
    app.config.globalProperties.$checkAuthC = () => useLoginStore().checkAuthC();
    app.config.globalProperties.$checkAuthR = () => useLoginStore().checkAuthR();
    app.config.globalProperties.$checkAuthU = () => useLoginStore().checkAuthU();
    app.config.globalProperties.$checkAuthD = () => useLoginStore().checkAuthD();

    app.config.globalProperties.$getErrorMsg = (
      label: string,
      type: "input" | "select" | "file"
    ) => {
      const getObjectPostposition = (word: string, type: "input" | "select" | "file"): string => {
        if (!word) return "";

        const lastChar = word[word.length - 1];
        const code = lastChar.charCodeAt(0);

        // 한글 범위 체크: '가' ~ '힣'
        if (code < 0xac00 || code > 0xd7a3) {
          // 한글이 아닐 경우 기본적으로 '를' 사용
          if (type === "input") {
            return t("message.endPoint1");
          } else if (type === "select") {
            return t("message.endPoint3");
          } else {
            return t("message.endPoint5");
          }
        }

        // 한글의 받침 유무 판단
        const baseCode = code - 0xac00;
        const jongseongIndex = baseCode % 28;

        if (type === "input") {
          return jongseongIndex === 0 ? t("message.endPoint1") : t("message.endPoint2");
        } else if (type === "select") {
          return jongseongIndex === 0 ? t("message.endPoint3") : t("message.endPoint4");
        } else {
          return jongseongIndex === 0 ? t("message.endPoint5") : t("message.endPoint6");
        }
      };

      if (locale.value === "ko") {
        return label + getObjectPostposition(label, type);
      } else {
        if (type === "input") {
          return t("message.endPoint1", [label]);
        } else {
          return t("message.endPoint2", [label]);
        }
      }
    };

    /**
     * 알림창 표시
     * @param _text - 표시할 메시지
     * @param _title - 제목 (선택사항)
     * @param callback - 확인 후 실행할 콜백 함수 (선택사항)
     */
    app.config.globalProperties.$alert = (
      _text: string,
      _title?: string,
      callback?: () => void
    ) => {
      const hasTitle = (_title ?? "") !== "";
      Swal.fire({
        ...(hasTitle ? { title: _title } : {}),
        html: _text,
        // confirmButtonColor: "#5D87FF",
        confirmButtonColor: "#000",
        reverseButtons: true,
        confirmButtonText: t("confirm.title"),
        scrollbarPadding: false,
        returnFocus: false,
      }).then(() => {
        if (callback && typeof callback === "function") {
          callback();
        }
      });
    };

    /**
     * BaseDialog 컴포넌트를 프로그래밍 방식으로 표시
     * @param message - 표시할 메시지
     */
    app.config.globalProperties.$lmsDialog = (message: string): Promise<void> => {
      return new Promise((resolve) => {
        const el = document.createElement("div");
        document.body.appendChild(el);

        const visible = ref(true);

        const close = () => {
          visible.value = false;
          setTimeout(() => {
            render(null, el);
            document.body.removeChild(el);
            resolve();
          }, 250);
        };

        const Wrapper = defineComponent({
          setup() {
            return () =>
              h(BaseDialog, {
                modelValue: visible.value,
                "onUpdate:modelValue": (v: boolean) => { visible.value = v; },
                message,
                showCancel: false,
                confirmText: "확인",
                onConfirm: close,
                onCancel: close,
              });
          },
        });

        const vnode = createVNode(Wrapper);
        vnode.appContext = (app as any)._context;
        render(vnode, el);
      });
    };

    /**
     * 확인창 표시
     * @param _text - 표시할 메시지
     * @param _title - 제목
     * @param callback - 선택 결과를 받는 콜백 함수
     */
    app.config.globalProperties.$confirm = (
      _text: string,
      _title: string,
      callback: (isConfirmed: boolean) => void
    ) => {
      const hasTitle = (_title ?? "") !== "";
      Swal.fire({
        ...(hasTitle ? { title: _title } : {}),
        html: _text,
        // confirmButtonColor: "#5D87FF",
        confirmButtonColor: "#000",
        showCancelButton: true,
        cancelButtonColor: "#fff",
        reverseButtons: true,
        confirmButtonText: t("confirm.title"),
        cancelButtonText: t("button.cancel"),
        scrollbarPadding: false,
      }).then((res: SweetAlertResult) => {
        if (callback && typeof callback === "function") {
          callback(res.isConfirmed);
        }
      });
    };

    /**
     * 토스트 메시지 표시
     * @param _title - 표시할 메시지
     * @param _icon - 아이콘 타입 (기본값: "info")
     */
    app.config.globalProperties.$toast = (_title: string, _icon: SweetAlertIcon = "info") => {
      const swalToast = Swal.mixin({
        toast: true,
        position: "top",
        showConfirmButton: false,
        timer: 3000,
        timerProgressBar: true,
        didOpen: (toast) => {
          toast.onmouseenter = Swal.stopTimer;
          toast.onmouseleave = Swal.resumeTimer;
        },
      });
      swalToast.fire({
        icon: _icon ?? "success",
        title: _title,
      });
    };

    /**
     * 데이터 테이블 파라미터 추출
     * @param options - 서버 옵션
     * @returns 요청용 서버 옵션
     */
    app.config.globalProperties.$serial = (options: ServerOptions) => {
      /*
       * page/rowsPerPage 는 반드시 유효한 양수여야 한다.
       * sessionStorage 에 남아 있던 옛 상태(키 누락/0/NaN)가 그대로 실려 나가면
       * BE 페이징 조건(<if test="rowsPerPage != null and rowsPerPage != 0">)이 꺼져
       * 전체 행이 내려오고 목록이 페이지네이션 없이 통째로 그려진다 → 기본값으로 보정.
       */
      const page = Number(options?.page);
      const rowsPerPage = Number(options?.rowsPerPage);
      const reqOptions: RequestServerOptions = {
        page: Number.isFinite(page) && page > 0 ? page : 1,
        rowsPerPage: Number.isFinite(rowsPerPage) && rowsPerPage > 0 ? rowsPerPage : 10,
        sortBy: Array.isArray(options.sortBy) ? options.sortBy.join() : options.sortBy,
        sortType: Array.isArray(options.sortType) ? options.sortType.join() : options.sortType,
      };
      return reqOptions;
    };

    /**
     * 날짜 비교 및 유효성 검사
     * @param strDate - 시작일
     * @param endDate - 종료일
     * @param resetField - 필드 초기화 함수
     */
    app.config.globalProperties.$compareDate = (
      strDate: string,
      endDate: string,
      resetField: () => void
    ) => {
      if (strDate && endDate && new Date(strDate) > new Date(endDate)) {
        app.config.globalProperties.$toast("시작일이 종료일보다 늦을 수 없습니다.", "info");
        resetField(); // 필드 초기화
      }
    };

    app.config.globalProperties.$setDetailValue = setDetailValue;
    app.config.globalProperties.$setSearchOption = setSearchOption;
    app.config.globalProperties.$setSearchOptionList = setSearchOptionList;
    app.config.globalProperties.$setOptionList = setOptionList;
    app.config.globalProperties.$setVueInfo = setVueInfo;

    /**
     * 필수 필드 유효성 검사
     * @param fields - 검사할 필드 배열
     * @param data - 검사할 데이터 객체
     * @returns 유효성 검사 통과 여부
     */
    app.config.globalProperties.$validateRequiredFields = (
      fields: DetailField[],
      data: Record<string, unknown>
    ) => {
      const vaild = (fields: DetailField[], data: Record<string, unknown>) => {
        const emptyKeys: string[] = [];
        let returnObj = {} as {
          isValid: boolean;
          error: {
            field: string;
            message: string;
          };
        };
        for (const field of fields) {
          if (!field.required && !field.maxLength && !field.rules) continue;

          const keys = Array.isArray(field.key) ? field.key : [field.key];
          for (const key of keys) {
            const value = data[key];
            const ruleFalseYn = field.rules?.(value as string) ?? true;
            if (field.type !== "file") {
              // required가 아닌 필드의 경우 빈 값은 허용하되, 값이 있을 때만 maxLength와 rules 검사
              if (field.required) {
                if (
                  value === "" ||
                  value === null ||
                  value === undefined ||
                  (Array.isArray(value) && value.length === 0)
                ) {
                  if (emptyKeys.length === 0) {
                    emptyKeys.push(key);
                  }
                } else if (!ruleFalseYn) {
                  if (emptyKeys.length === 0) {
                    emptyKeys.push(key);
                  }
                }
              } else {
                // required가 아닌 필드는 값이 있을 때만 검사
                if (
                  value !== "" &&
                  value !== null &&
                  value !== undefined &&
                  !(Array.isArray(value) && value.length === 0)
                ) {
                  if (!ruleFalseYn) {
                    if (emptyKeys.length === 0) {
                      emptyKeys.push(key);
                    }
                  }
                }
              }
            } else {
              if (emptyKeys.length === 0) {
                if (field.referFiles?.length === 0) {
                  emptyKeys.push(key);
                }
              }
            }
            if (emptyKeys.length > 0 && Object.keys(returnObj).length === 0) {
              const fieldName = field.label || emptyKeys.join(", ");
              let msgType = "input";
              if (field.type === "file") {
                msgType = "file";
              } else if (
                !["number", "text", "id", "pwd", "longText", "textarea"].includes(field.type)
              ) {
                msgType = "select";
              }
              let msg = app.config.globalProperties.$getErrorMsg(
                fieldName,
                msgType as "input" | "select" | "file"
              );
              if (!ruleFalseYn) {
                msg = t("validation.rules");
              }
              returnObj = {
                isValid: false,
                error: {
                  field: emptyKeys[0],
                  message: msg,
                },
              };
              return returnObj;
            }
          }
        }
        if (emptyKeys.length > 0 && Object.keys(returnObj).length !== 0) {
          return returnObj;
        }
        return {
          isValid: true,
          error: {
            field: "",
            message: "",
          },
        };
      };
      const vailDate = vaild(fields, data);
      if (!vailDate.isValid) {
        const element = document.getElementById(vailDate.error.field) as HTMLElement;
        if (element) {
          element.focus();
        }
        app.config.globalProperties.$toast(vailDate.error.message, "error");
      }
      return vailDate.isValid;
    };

    /**
     * 이전 페이지로 이동
     * @param _fallbackPath - 대체 경로 (기본값: "")
     */
    app.config.globalProperties.$goBack = (_fallbackPath: string = "") => {
      if (window.history.state.back && _fallbackPath === "") {
        window.history.back();
      } else {
        window.location.href = _fallbackPath;
      }
    };

    /**
     * 기존 키가 있는 값만 복사
     * @param source - 원본 객체
     * @param target - 대상 객체
     */
    app.config.globalProperties.$copyExistKeys = (source: unknown, target: ParamsType) => {
      const result = source as ParamsType;
      for (const key in result) {
        if (key in target) {
          result[key] = target[key];
        }
      }
    };

    /**
     * 폼데이터 형식으로 변환
     * @param param - 원본 객체
     * @param insertField
     * @param tableName
     */
    app.config.globalProperties.$setFormData = (
      param: unknown,
      insertField: DetailField[],
      tableName: string
    ) => {
      const formData = new FormData() as FormData;
      formData.append("param", new Blob([JSON.stringify(param)], { type: "application/json" }));
      const fileTypesArray: string[] = [];
      insertField
        .filter((field) => field.type === "file" && field.referFiles && field.referFiles.length > 0)
        .forEach((field) => {
          const key = field.key as string;
          field.referFiles?.forEach((file) => {
            formData.append("files", file);
            fileTypesArray.push(key); // 중복 없이 key 저장
          });
        });

      const fileMeta = {
        tableName: tableName,
        typeList: fileTypesArray,
      } as FileMetaTypes;
      formData.append(
        "fileMeta",
        new Blob([JSON.stringify(fileMeta)], { type: "application/json" })
      );
      return formData;
    };
  },
};
